import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CheckCircle, Loader2, RefreshCw, Search } from 'lucide-react';
import api from '../api';

const S = `
  .si-wrap { font-family: 'Inter', sans-serif; color: #000; }
  .si-top { display:flex; justify-content:space-between; align-items:center; gap:12px; flex-wrap:wrap; margin-bottom:14px; }
  .si-title { margin:0; font-size:22px; font-weight:800; color:#111827; }
  .si-sub { margin-top:4px; font-size:13px; color:#1a1a1a; }
  .si-toolbar { display:flex; gap:10px; align-items:center; flex-wrap:wrap; margin-bottom:12px; }
  .si-search { position:relative; min-width:260px; flex:1; }
  .si-search input { width:100%; box-sizing:border-box; border:1px solid #d1d5db; border-radius:10px; padding:10px 12px 10px 32px; font-size:13px; }
  .si-table-wrap { background:#fff; border:1px solid #e5e7eb; border-radius:14px; box-shadow:0 2px 10px rgba(0,0,0,.05); overflow:auto; }
  .si-table { width:100%; border-collapse:collapse; min-width:960px; }
  .si-table th { text-align:left; padding:11px 12px; font-size:11px; letter-spacing:.4px; color:#1a1a1a; border-bottom:2px solid #f3f4f6; text-transform:uppercase; }
  .si-table td { padding:12px; font-size:13px; color:#000; border-bottom:1px solid #f9fafb; }
  .si-badge { display:inline-flex; align-items:center; padding:4px 10px; border-radius:999px; font-size:11px; font-weight:700; }
  .si-btn { border:1px solid #d1d5db; border-radius:8px; background:#fff; color:#111827; padding:7px 10px; font-size:12px; font-weight:700; cursor:pointer; min-height:38px; }
  .si-btn:disabled { opacity:.55; cursor:not-allowed; }
  .si-btn-success { background:#16a34a; border-color:#16a34a; color:#fff; }
  .si-empty { padding:40px 12px; text-align:center; color:#1a1a1a; }

  .si-modal-overlay { position:fixed; inset:0; background:rgba(0,0,0,.45); z-index:1200; display:flex; align-items:center; justify-content:center; padding:16px; }
  .si-modal { width:100%; max-width:430px; background:#fff; border-radius:14px; box-shadow:0 12px 28px rgba(0,0,0,.25); }
  .si-modal-head { padding:14px 16px; border-bottom:1px solid #e5e7eb; font-size:16px; font-weight:800; color:#111827; }
  .si-modal-body { padding:14px 16px; display:grid; gap:10px; }
  .si-modal-body label { font-size:12px; color:#1a1a1a; font-weight:700; }
  .si-modal-body input, .si-modal-body textarea { border:1px solid #d1d5db; border-radius:9px; padding:9px 10px; font-size:13px; color:#111827; }
  .si-modal-foot { padding:12px 16px; border-top:1px solid #e5e7eb; display:flex; justify-content:flex-end; gap:8px; }
`;

const gradeBadge = (grade) => {
  if (grade === 'A') return { bg: '#dcfce7', color: '#166534' };
  if (grade === 'B') return { bg: '#fef3c7', color: '#92400e' };
  if (grade === 'C') return { bg: '#fee2e2', color: '#991b1b' };
  return { bg: '#e5e7eb', color: '#374151' };
};

function DeliveryModal({ item, submitting, onClose, onConfirm }) {
  const [deliveryDate, setDeliveryDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState('');
  if (!item) return null;
  return (
    <div className="si-modal-overlay" onClick={onClose}>
      <div className="si-modal" onClick={(e) => e.stopPropagation()}>
        <div className="si-modal-head">Mark Delivered</div>
        <div className="si-modal-body">
          <div style={{ fontSize: 13, color: '#000' }}>
            <strong>{item.design_name || item.design_code}</strong> (JC-{item.job_card_id})
          </div>
          <label>Delivery Date</label>
          <input type="date" value={deliveryDate} onChange={(e) => setDeliveryDate(e.target.value)} />
          <label>Delivery Notes</label>
          <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional notes" />
        </div>
        <div className="si-modal-foot">
          <button className="si-btn" onClick={onClose}>Cancel</button>
          <button className="si-btn si-btn-success" disabled={submitting} onClick={() => onConfirm({ deliveryDate, notes })}>
            {submitting ? 'Saving...' : 'Confirm Delivery'}
          </button>
        </div>
      </div>
    </div>
  );
}

const fmtDate = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

export default function StoreInventory() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/sales/store-inventory');
      setRows(res?.data?.data || []);
    } catch (e) {
      setRows([]);
      setError(e?.response?.data?.message || 'Failed to load store inventory.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) => {
      const text = [row.design_name, row.design_code, row.customer_name, `jc-${row.job_card_id}`]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return text.includes(q);
    });
  }, [rows, query]);

  const markDelivered = async ({ deliveryDate, notes }) => {
    if (!selected) return;
    setSubmitting(true);
    try {
      await api.post(`/sales/orders/${selected.job_card_id}/mark-delivered`, {
        delivery_date: `${deliveryDate}T00:00:00Z`,
        delivery_notes: notes,
      });
      setSelected(null);
      await load();
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to mark delivered.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <style>{S}</style>
      <div className="si-wrap">
        <div className="si-top">
          <div>
            <h2 className="si-title">Store Inventory</h2>
            <div className="si-sub">Completed items ready for client delivery.</div>
          </div>
          <button className="si-btn" onClick={load}><RefreshCw size={14} style={{ marginRight: 6 }} />Refresh</button>
        </div>

        <div className="si-toolbar">
          <div className="si-search">
            <Search size={14} style={{ position: 'absolute', left: 10, top: 11, color: '#9ca3af' }} />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search design/client/job card" />
          </div>
        </div>

        {loading ? (
          <div className="si-empty"><Loader2 size={18} className="animate-spin" /> Loading store inventory...</div>
        ) : error ? (
          <div className="si-empty" style={{ color: '#b91c1c' }}>{error}</div>
        ) : filtered.length === 0 ? (
          <div className="si-empty">No store inventory items found.</div>
        ) : (
          <div className="si-table-wrap">
            <table className="si-table">
              <thead>
                <tr>
                  <th>Design</th>
                  <th>Pieces</th>
                  <th>Weight</th>
                  <th>QC Grade</th>
                  <th>Received Date</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((row) => {
                  const grade = gradeBadge(row.qc_grade);
                  const delivered = (row.delivery_status || 'PENDING') === 'DELIVERED';
                  return (
                    <tr key={`${row.id}-${row.job_card_id}`}>
                      <td>
                        <div style={{ fontWeight: 700, color: '#111827' }}>{row.design_name || row.design_code}</div>
                        <div style={{ fontSize: 11, color: '#1a1a1a' }}>JC-{row.job_card_id}</div>
                      </td>
                      <td>{row.pieces_available ?? row.quantity ?? '-'}</td>
                      <td>{row.total_weight ?? row.final_weight_g ?? '-'} g</td>
                      <td><span className="si-badge" style={{ background: grade.bg, color: grade.color }}>{row.qc_grade || '-'}</span></td>
                      <td>{fmtDate(row.added_at || row.created_at || row.qc_passed_at)}</td>
                      <td>
                        <span className="si-badge" style={{ background: delivered ? '#dcfce7' : '#f3f4f6', color: delivered ? '#166534' : '#374151' }}>
                          {delivered ? 'DELIVERED' : 'PENDING'}
                        </span>
                      </td>
                      <td>
                        {!delivered ? (
                          <button className="si-btn si-btn-success" onClick={() => setSelected(row)}>
                            <CheckCircle size={13} style={{ marginRight: 6 }} />Mark Delivered
                          </button>
                        ) : (
                          <span style={{ color: '#1a1a1a', fontSize: 12 }}>Delivered</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <DeliveryModal item={selected} submitting={submitting} onClose={() => setSelected(null)} onConfirm={markDelivered} />
    </>
  );
}
